import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SlideOverShell } from '../../ui/slideOverParts';
import { borderGrey, canvasBg, primaryText, secondaryText, textDarkGreen } from '../../../styling/stylingHelper';

/** Native dialog supplies focus containment and restores focus to the guide button. */
export default function SetupForwardingGuide({ provider, alias, onClose, footer }: {
  provider: string; alias: string; onClose: () => void; footer: React.ReactNode;
}) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const base = 'accountSetup.forwarding';
  const guide = `${base}.guides.${provider}`;
  const sectionCopy = provider === 'gmail' ? guide : base;
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => { element?.close(); };
  }, []);
  const list = (key: string) => <ol className={`list-decimal space-y-3 pl-5 text-sm leading-6 break-words ${secondaryText}`}>
    {(t(key, { returnObjects: true, alias }) as string[]).map((text, index) => <li key={index}>{text}</li>)}
  </ol>;
  return createPortal(<dialog ref={dialog} aria-label={`${t(`${base}.guideTitle`)} · ${t(`${guide}.name`)}`} onCancel={event => { event.preventDefault(); onClose(); }} className={`m-0 h-full w-full max-h-none max-w-none border-0 bg-transparent dark:bg-transparent p-0 backdrop:bg-transparent dark:backdrop:bg-transparent ${primaryText}`}>
    <SlideOverShell onClose={onClose} widthClass="max-w-full lg:max-w-[50vw]" header={<span className="ml-3 font-semibold">{t(`${guide}.name`)}</span>}>
      <article className="space-y-6">
        <header><h2 className="text-xl font-semibold">{t(`${base}.guideTitle`)}</h2><p className={`mt-2 text-sm ${secondaryText}`}>{t(`${base}.intro`)}</p></header>
        <div className={`rounded-xl p-4 ${canvasBg}`}><p className={`text-xs ${secondaryText}`}>{t(`${base}.aliasLabel`)}</p><p className={`mt-1 break-all font-semibold ${textDarkGreen}`}>{alias}</p></div>
        {provider === 'gmail' && <>
          <div className={`rounded-xl p-4 text-sm leading-6 ${canvasBg} ${secondaryText}`}>{t(`${guide}.workspaceIntro`, { personalLocation: t(`${guide}.personalLocation`) })}</div>
          <section className="space-y-3">
            <h3 className="font-semibold">{t(`${guide}.adminTitle`)}</h3>
            <p className={`text-sm leading-6 ${secondaryText}`}>{t(`${guide}.adminHelp`)}</p>
            {list(`${guide}.adminSteps`)}
            <p className={`text-sm leading-6 ${secondaryText}`}>{t(`${guide}.adminDelay`)}</p>
          </section>
        </>}
        <section><h3 className="mb-3 font-semibold">{t(`${sectionCopy}.permissions`)}</h3>{list(`${guide}.access`)}</section>
        <section className="space-y-4">
          <h3 className="font-semibold">{t(`${sectionCopy}.rules`)}</h3>
          <p className={`text-sm leading-6 ${secondaryText}`}>{t(`${base}.allMailHelp`)}</p>
          {list(`${guide}.alternative`)}
          <p className={`text-sm leading-6 ${secondaryText}`}>{t(`${base}.oneMethod`)}</p>
        </section>
        <section><h3 className="mb-3 font-semibold">{t(`${sectionCopy}.test`)}</h3>{list(`${base}.checklist`)}</section>
        <details className={`rounded-xl border p-4 ${borderGrey}`}>
          <summary className={`min-h-[44px] cursor-pointer font-semibold ${textDarkGreen}`}>{t(`${base}.optionalTitle`)}</summary>
          <p className={`my-3 text-sm leading-6 ${secondaryText}`}>{t(`${sectionCopy}.selectedMailHelp`)}</p>
          {list(`${guide}.steps`)}
          <p className={`mt-3 text-sm leading-6 ${secondaryText}`}>{t(`${base}.selectedTest`)}</p>
          {provider === 'outlook' && <figure className="mt-4">
            <svg viewBox="0 0 1432 655" role="img" aria-label={t(`${base}.screenshotRule`)} className="w-full rounded-lg">
              <image href="/guides/email-forwarding/outlook-rule.png" width="1432" height="655" />
              {/* Compose the customer's address over the example's destination field. */}
              <rect x="824" y="382" width="375" height="43" fill="white" />
              <text x="835" y="407" fill="#222" fontFamily="Arial, sans-serif" fontSize={Math.min(19, 540 / alias.length)}>{alias}</text>
            </svg><figcaption className={`mt-2 text-xs ${secondaryText}`}>{t(`${base}.screenshotRule`)}</figcaption>
          </figure>}
        </details>
        {provider === 'gmail' && <section className="space-y-3">
          <h3 className="font-semibold">{t(`${guide}.personalTitle`)}</h3>
          <p className={`text-sm leading-6 ${secondaryText}`}>{t(`${guide}.personalHelp`)}</p>
        </section>}
        <details className={`rounded-xl border p-4 ${borderGrey}`}>
          <summary className={`min-h-[44px] cursor-pointer font-medium ${textDarkGreen}`}>{t(`${base}.troubleshooting`)}</summary>
          {list(`${guide}.problems`)}
          {provider === 'outlook' && <figure className="mt-4"><img src="/guides/email-forwarding/outlook-permissions.png" alt={t(`${base}.screenshotPermissions`)} className="w-full max-w-md rounded-lg" /><figcaption className={`mt-2 text-xs ${secondaryText}`}>{t(`${base}.screenshotPermissions`)}</figcaption></figure>}
          <h3 className="mt-5 text-sm font-semibold">{t(`${base}.sources`)}</h3>
          <ul className="space-y-1 text-sm">{(t(`${guide}.sources`, { returnObjects: true }) as { label: string; url: string }[]).map(source => <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer" className={`inline-flex min-h-[44px] items-center underline ${textDarkGreen}`}>{source.label}</a></li>)}</ul>
          <p className={`mt-2 text-xs ${secondaryText}`}>{t(`${sectionCopy}.checked`)}</p>
        </details>
        <div className={`border-t pt-4 ${borderGrey}`}>{footer}</div>
      </article>
    </SlideOverShell>
  </dialog>, document.body);
}
